import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Space, 
  Tag, 
  Table, 
  Progress,
  Alert,
  Tooltip,
  Modal,
  Input,
  message,
  Divider
} from 'antd';
import { 
  ShareAltOutlined, 
  CopyOutlined, 
  DollarOutlined, 
  UserOutlined, 
  TrophyOutlined,
  GiftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { referralService } from '../../../services/referralService';

const { Title, Text, Paragraph } = Typography;

const ReferralDashboard = () => {
  const { currentUser, userProfile } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [referralHistory, setReferralHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shareModalVisible, setShareModalVisible] = useState(false);

  useEffect(() => {
    if (currentUser) {
      fetchReferralData();
    }
  }, [currentUser]);

  const fetchReferralData = async () => {
    try {
      setLoading(true);
      
      // Get or create referral data
      let data = await referralService.getReferralData(currentUser.uid);
      if (!data) {
        // Create referral code for new user
        data = await referralService.createReferralCode(currentUser.uid, userProfile);
      }
      
      setReferralData(data);
      
      // Get referral history
      const history = await referralService.getUserReferralHistory(currentUser.uid);
      setReferralHistory(history);
    } catch (error) {
      console.error('Error fetching referral data:', error);
      message.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const handleShareReferral = () => {
    setShareModalVisible(true);
  };

  const handleCopyReferralLink = () => {
    const referralLink = `${window.location.origin}/signup?ref=${referralData.referralCode}`;
    navigator.clipboard.writeText(referralLink);
    message.success('Referral link copied to clipboard!');
  };

  const handleCopyReferralCode = () => {
    navigator.clipboard.writeText(referralData.referralCode);
    message.success('Referral code copied to clipboard!');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      completed: 'green',
      expired: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <ClockCircleOutlined />,
      completed: <CheckCircleOutlined />,
      expired: <ExclamationCircleOutlined />
    };
    return icons[status] || <ClockCircleOutlined />;
  };

  const columns = [
    {
      title: 'Referred User',
      dataIndex: 'newUserData',
      key: 'newUserData',
      render: (userData) => (
        <div>
          <Text strong>{userData.firstName} {userData.lastName}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {userData.email}
          </Text>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} icon={getStatusIcon(status)}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Reward',
      dataIndex: 'rewardAmount',
      key: 'rewardAmount',
      render: (amount, record) => (
        <div>
          <Text strong style={{ color: record.status === 'completed' ? '#52c41a' : '#faad14' }}>
            ${amount}
          </Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.status === 'completed' ? 'Earned' : 'Pending'}
          </Text>
        </div>
      )
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  if (loading) {
    return <div>Loading referral dashboard...</div>;
  }

  if (!referralData) {
    return <div>Failed to load referral data</div>;
  }

  const referralLink = `${window.location.origin}/signup?ref=${referralData.referralCode}`;
  const completionRate = referralData.totalReferrals > 0 
    ? (referralData.successfulReferrals / referralData.totalReferrals * 100).toFixed(1)
    : 0;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <Title level={2}>Referral Program</Title>
        <Paragraph>
          Earn $25 for every friend you refer who signs up for our services!
        </Paragraph>
      </div>

      {/* Referral Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '2rem' }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Referrals"
              value={referralData.totalReferrals}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Successful Referrals"
              value={referralData.successfulReferrals}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Earnings"
              value={referralData.totalEarnings}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix=""
            />
          </Card>
        </Col>
      </Row>

      {/* Referral Code & Sharing */}
      <Card style={{ marginBottom: '2rem' }}>
        <Title level={4}>Your Referral Code</Title>
        <div style={{ marginBottom: '1rem' }}>
          <Input.Group compact>
            <Input
              value={referralData.referralCode}
              readOnly
              style={{ width: '200px' }}
            />
            <Button 
              icon={<CopyOutlined />}
              onClick={handleCopyReferralCode}
            >
              Copy Code
            </Button>
          </Input.Group>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <Input.Group compact>
            <Input
              value={referralLink}
              readOnly
              style={{ width: 'calc(100% - 120px)' }}
            />
            <Button 
              icon={<CopyOutlined />}
              onClick={handleCopyReferralLink}
            >
              Copy Link
            </Button>
          </Input.Group>
        </div>

        <Space>
          <Button 
            type="primary" 
            icon={<ShareAltOutlined />}
            onClick={handleShareReferral}
          >
            Share Referral
          </Button>
          <Button icon={<GiftOutlined />}>
            View Rewards
          </Button>
        </Space>
      </Card>

      {/* Progress & Pending */}
      <Row gutter={[16, 16]} style={{ marginBottom: '2rem' }}>
        <Col xs={24} lg={12}>
          <Card>
            <Title level={5}>Referral Success Rate</Title>
            <Progress
              percent={completionRate}
              strokeColor="#52c41a"
              format={() => `${completionRate}%`}
            />
            <Text type="secondary">
              {referralData.successfulReferrals} of {referralData.totalReferrals} referrals completed
            </Text>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card>
            <Title level={5}>Pending Rewards</Title>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ color: '#faad14', margin: 0 }}>
                ${referralData.pendingEarnings}
              </Title>
              <Text type="secondary">
                {referralData.totalReferrals - referralData.successfulReferrals} pending referrals
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      {/* How It Works */}
      <Card style={{ marginBottom: '2rem' }}>
        <Title level={4}>How It Works</Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>1️⃣</div>
              <Title level={5}>Share Your Code</Title>
              <Text type="secondary">
                Share your unique referral code or link with friends and family
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>2️⃣</div>
              <Title level={5}>They Sign Up</Title>
              <Text type="secondary">
                Your friend signs up using your referral code
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>3️⃣</div>
              <Title level={5}>You Earn $25</Title>
              <Text type="secondary">
                When they complete their first service, you earn $25 credit
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Referral History */}
      <Card>
        <Title level={4}>Referral History</Title>
        {referralHistory.length > 0 ? (
          <Table
            columns={columns}
            dataSource={referralHistory}
            rowKey="id"
            pagination={{ pageSize: 5 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <UserOutlined style={{ fontSize: '3rem', color: '#d9d9d9', marginBottom: '1rem' }} />
            <Title level={4} type="secondary">No referrals yet</Title>
            <Text type="secondary">
              Start sharing your referral code to earn rewards!
            </Text>
          </div>
        )}
      </Card>

      {/* Share Modal */}
      <Modal
        title="Share Your Referral"
        open={shareModalVisible}
        onCancel={() => setShareModalVisible(false)}
        footer={null}
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '1rem 0' }}>
          <Title level={4}>Share and Earn!</Title>
          <Paragraph>
            Share your referral code with friends and earn $25 for each successful referral.
          </Paragraph>
          
          <div style={{ margin: '1.5rem 0' }}>
            <Text strong>Your Referral Code:</Text>
            <br />
            <Text code style={{ fontSize: '1.2rem', padding: '0.5rem' }}>
              {referralData.referralCode}
            </Text>
          </div>

          <div style={{ margin: '1.5rem 0' }}>
            <Text strong>Referral Link:</Text>
            <br />
            <Text code style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
              {referralLink}
            </Text>
          </div>

          <Space>
            <Button 
              type="primary" 
              icon={<CopyOutlined />}
              onClick={handleCopyReferralLink}
            >
              Copy Link
            </Button>
            <Button 
              icon={<ShareAltOutlined />}
              onClick={() => {
                if (navigator.share) {
                  navigator.share({
                    title: 'Join HSP',
                    text: 'I love my home maintenance service! Use my referral code for a discount.',
                    url: referralLink
                  });
                }
              }}
            >
              Share
            </Button>
          </Space>
        </div>
      </Modal>
    </div>
  );
};

export default ReferralDashboard;
